import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { encrypt } from "../../utils/encryption";
import Button from "../../components/common/Button";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";

function CustomerListPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const basePath = useMemo(() => {
    if (location.pathname.includes("/settings-master/customer-master")) return "/settings-master/customer-master";
    return "/customer-master";
  }, [location.pathname]);

  // --- CRUD Hook ---
  const endpoints = useMemo(
    () => ({
      list: "/customer-master",
      delete: (id) => `/customer-master/${id}`,
    }),
    []
  );

  const {
    rows,
    isLoading,
    error: listError,
    filters,
    pagination,
    sortConfig,
    askDelete,
    changePage,
    changeSort,
    updateFilters,
  } = useCrud({
    endpoints,
    idKey: "customerId",
    initialFilters: { search: "" },
    initialSort: { key: "customerName", order: "ASC" },
  });

  const openCreate = () => navigate(`${basePath}/new`);
  const openEdit = (item) => navigate(`${basePath}/${encrypt(item?.customerId)}/edit`);

  const tableColumns = useMemo(
    () => [
      { key: "customerName", label: "Customer Name", sortable: true },
      { key: "customerCode", label: "Code", sortable: true },
      { key: "companyName", label: "Company", sortable: true },
      { key: "email", label: "Email", sortable: true },
      {
        key: "country",
        label: "Country",
        sortable: true,
        render: (item) => item?.country?.countryName || item?.countryName || "-",
      },
      {
        key: "status",
        label: "Status",
        render: (item) => (
          <span
            className={`rounded-full px-2 py-1 text-xs font-semibold ${
              item?.isActive
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
            }`}
          >
            {item?.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
            Customer <span className="text-primary-600">Master</span>
          </h1>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">
            Manage your customer database and contact details
          </p>
        </div>
        <Button onClick={openCreate} className="shadow-xl shadow-primary-500/10">
          <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Create Customer
        </Button>
      </div>

      {listError ? <ErrorAlert message={listError} /> : null}

      <CrudTable
        columns={tableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        onEdit={openEdit}
        onDelete={askDelete}
        searchKey="search"
        searchPlaceholder="Search by name, code or company"
        tableScrollHeight="58vh"
      />
    </div>
  );
}

export default CustomerListPage;
